/* ==========================================================================
   FILE: assets/casa-delacruz-signup.js

   PURPOSE:
   - Handles button state -> form state replacement
   - Validates Email and SMS fields on the front end
   - Sends data directly to GoHighLevel webhook URLs
   - Displays success/error messages in reserved feedback space
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  const signupSections = document.querySelectorAll('.dc-signup-section');

  signupSections.forEach((section) => {
    const emailWebhook = section.dataset.emailWebhook || '';
    const smsWebhook = section.dataset.smsWebhook || '';

    const toggles = section.querySelectorAll('.dc-signup-toggle');
    const closeButtons = section.querySelectorAll('.dc-signup-back');
    const forms = section.querySelectorAll('.dc-signup-form');

    /* ----------------------------------------------------------------------
       Utility: show selected panel, replacing the button state
       ---------------------------------------------------------------------- */
    function openPanel(type) {
      const column = section.querySelector(`.dc-signup-column[data-signup-type="${type}"]`);
      if (!column) return;

      const buttonState = column.querySelector('.dc-signup-state--button');
      const formState = column.querySelector('.dc-signup-state--form');
      const toggle = column.querySelector('.dc-signup-toggle');
      const input = column.querySelector('.dc-signup-input');

      if (buttonState) buttonState.hidden = true;
      if (formState) formState.hidden = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (input) {
        window.setTimeout(() => input.focus(), 100);
      }
    }

    /* ----------------------------------------------------------------------
       Utility: close selected panel and restore button state
       ---------------------------------------------------------------------- */
    function closePanel(type) {
      const column = section.querySelector(`.dc-signup-column[data-signup-type="${type}"]`);
      if (!column) return;

      const buttonState = column.querySelector('.dc-signup-state--button');
      const formState = column.querySelector('.dc-signup-state--form');
      const toggle = column.querySelector('.dc-signup-toggle');
      const form = column.querySelector('.dc-signup-form');

      if (form) {
        form.reset();
        resetMessages(form);
        enableForm(form);
      }

      if (buttonState) buttonState.hidden = false;
      if (formState) formState.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    /* ----------------------------------------------------------------------
       Utility: clear all message states before validation/submission
       ---------------------------------------------------------------------- */
    function resetMessages(form) {
      const success = form.querySelector('.dc-signup-message--success');
      const error = form.querySelector('.dc-signup-message--error');

      if (success) success.hidden = true;
      if (error) error.hidden = true;

      form.classList.remove('is-submitting', 'is-success', 'has-error');
    }

    /* ----------------------------------------------------------------------
       Utility: show error text inside reserved feedback space
       ---------------------------------------------------------------------- */
    function showError(form, message) {
      const error = form.querySelector('.dc-signup-message--error');
      const success = form.querySelector('.dc-signup-message--success');

      if (success) success.hidden = true;

      if (error) {
        error.textContent = message;
        error.hidden = false;
      }

      form.classList.add('has-error');
    }

    /* ----------------------------------------------------------------------
       Utility: show success text inside reserved feedback space
       ---------------------------------------------------------------------- */
    function showSuccess(form) {
      const success = form.querySelector('.dc-signup-message--success');
      const error = form.querySelector('.dc-signup-message--error');

      if (error) error.hidden = true;
      if (success) success.hidden = false;

      form.classList.add('is-success');
    }

    /* ----------------------------------------------------------------------
       Utility: disable / enable form after submit
       ---------------------------------------------------------------------- */
    function disableForm(form) {
      const input = form.querySelector('.dc-signup-input');
      const submit = form.querySelector('.dc-signup-submit');

      if (input) input.disabled = true;
      if (submit) submit.disabled = true;
    }

    function enableForm(form) {
      const input = form.querySelector('.dc-signup-input');
      const submit = form.querySelector('.dc-signup-submit');

      if (input) input.disabled = false;
      if (submit) submit.disabled = false;
    }

    /* ----------------------------------------------------------------------
       Validation helpers
       ---------------------------------------------------------------------- */
    function isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function normalizePhone(phone) {
      const digits = phone.replace(/\D/g, '');

      /* If 10 digits, prepend US country code for cleaner GHL handling */
      if (digits.length === 10) {
        return `+1${digits}`;
      }

      /* If already 11+ digits, just add + back in front */
      if (digits.length >= 11) {
        return `+${digits}`;
      }

      return phone;
    }

    function isValidPhone(phone) {
      const digits = phone.replace(/\D/g, '');
      return digits.length >= 10;
    }

    /* ----------------------------------------------------------------------
       Button clicks: swap button state for form state
       ---------------------------------------------------------------------- */
    toggles.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.dataset.target;
        openPanel(target);
      });
    });

    /* ----------------------------------------------------------------------
       Back buttons: restore original CTA button
       ---------------------------------------------------------------------- */
    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.dataset.close;
        closePanel(target);
      });
    });

    /* ----------------------------------------------------------------------
       Form submission
       ---------------------------------------------------------------------- */
    forms.forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        resetMessages(form);

        const type = form.dataset.formType;
        const endpoint = type === 'sms' ? smsWebhook : emailWebhook;

        const emailInput = form.querySelector('input[name="email"]');
        const phoneInput = form.querySelector('input[name="phone"]');

        const email = emailInput ? emailInput.value.trim() : '';
        const phone = phoneInput ? phoneInput.value.trim() : '';

        /* --------------------------------------------------------------
           Front-end validation
           -------------------------------------------------------------- */
        if (type === 'email') {
          if (!email) {
            showError(form, 'Please enter your email address.');
            return;
          }

          if (!isValidEmail(email)) {
            showError(form, 'Please enter a valid email address.');
            return;
          }
        }

        if (type === 'sms') {
          if (!phone) {
            showError(form, 'Please enter your mobile phone number.');
            return;
          }

          if (!isValidPhone(phone)) {
            showError(form, 'Please enter a valid phone number.');
            return;
          }
        }

        if (!endpoint) {
          showError(form, 'This form is not connected yet. Add the GoHighLevel webhook URL in the section settings.');
          return;
        }

        /* --------------------------------------------------------------
           Populate hidden page metadata fields before sending
           -------------------------------------------------------------- */
        const pagePathField = form.querySelector('input[name="page_path"]');
        const pageUrlField = form.querySelector('input[name="page_url"]');

        if (pagePathField) pagePathField.value = window.location.pathname;
        if (pageUrlField) pageUrlField.value = window.location.href;

        const formData = new FormData(form);
        const payload = Object.fromEntries(formData.entries());

        /* Normalize phone before sending to GHL */
        if (payload.phone) {
          payload.phone = normalizePhone(payload.phone);
        }

        try {
          form.classList.add('is-submitting');

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            throw new Error('Request failed');
          }

          form.classList.remove('is-submitting');
          showSuccess(form);
          disableForm(form);
        } catch (error) {
          form.classList.remove('is-submitting');
          showError(form, 'Something went wrong. Please try again.');
        }
      });
    });
  });
});